import React, {
  useState,
  useContext,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import BaseDraggable from "./BaseDraggable";
import { CanvasScaleContext } from "../components/Form/Canvas/ScaleContext";

export default function ToggleButtonItemLite({
  id,
  position,
  toggled = false,
  width = 100,
  height = 40,
  opacity = 1,
  borderRadius = 8,
  borderWidth = 2,
  backgroundColor = "#ffffff",
  borderColor = "#000000",
  corners = 4,
  // Text styling
  fontFamily = "Arial, sans-serif",
  fontSize = 14,
  fontStyles = {
    bold: true,
    italic: false,
    underline: false,
    lineThrough: false,
  },
  fontColor = "#000000",
  onDelete,
  setToggleValue,
  setSize,
  onDoubleClick,
  // optional persisted shape data
  anchors: savedAnchors,
  handles: savedHandles,
  // callback to persist updates into form state
  onUpdate,
}) {
  const canvasScale = useContext(CanvasScaleContext) || 1;
  const [isActive, setIsActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState(null);
  const [isSubDragActive, setIsSubDragActive] = useState(false);
  const svgRef = useRef(null);
  const pathRef = useRef(null);
  const anchorsRef = useRef(null);
  const handlesRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const isResizingRef = useRef(false);

  const padding = useMemo(() => Math.max(2, borderWidth), [borderWidth]);

  // Dynamic dimensions and offset state
  const [dynamicDimensions, setDynamicDimensions] = useState({
    width,
    height,
  });
  const [contentOffset, setContentOffset] = useState({ x: 0, y: 0 });

  // Default anchors generator (rectangle for 4, radial for 3+)
  const generateDefaultAnchors = useCallback(
    (n, w, h) => {
      const cx = w / 2;
      const cy = h / 2;
      const rx = Math.max(0, w / 2 - padding);
      const ry = Math.max(0, h / 2 - padding);
      if (n === 4) {
        return [
          { x: padding, y: padding },
          { x: w - padding, y: padding },
          { x: w - padding, y: h - padding },
          { x: padding, y: h - padding },
        ];
      }
      const pts = [];
      for (let i = 0; i < n; i++) {
        const angle = (2 * Math.PI * i) / n - Math.PI / 2;
        pts.push({
          x: cx + rx * Math.cos(angle),
          y: cy + ry * Math.sin(angle),
        });
      }
      return pts;
    },
    [padding]
  );

  // Shape state: anchors and cubic handles
  const [anchors, setAnchors] = useState(() => {
    if (Array.isArray(savedAnchors) && savedAnchors.length === corners) {
      return savedAnchors.map((p) => ({ x: p.x, y: p.y }));
    }
    return generateDefaultAnchors(corners || 4, width, height);
  });
  const [handles, setHandles] = useState(() => {
    const count = corners || 4;
    if (Array.isArray(savedHandles) && savedHandles.length === count) {
      return savedHandles.map((h) => ({
        left: { x: h.left?.x || 0, y: h.left?.y || 0 },
        right: { x: h.right?.x || 0, y: h.right?.y || 0 },
      }));
    }
    return Array.from({ length: count }, () => ({
      left: { x: 0, y: 0 },
      right: { x: 0, y: 0 },
    }));
  });

  // Keep refs updated for persistence in event handlers
  useEffect(() => {
    anchorsRef.current = anchors;
  }, [anchors]);
  useEffect(() => {
    handlesRef.current = handles;
  }, [handles]);

  // Hide active handles when double-click occurs outside the component
  useEffect(() => {
    const onDblClickOutside = (e) => {
      // Note: wrapperRef may be undefined in this build; keep safe
      // We only clear if click is outside our component box
      const wrapper = svgRef.current?.parentElement;
      if (wrapper && !wrapper.contains(e.target)) {
        setActiveAnchor(null);
      }
    };
    window.addEventListener("dblclick", onDblClickOutside);
    return () => window.removeEventListener("dblclick", onDblClickOutside);
  }, []);

  // Sync from props/corners changes
  useEffect(() => {
    if (Array.isArray(savedAnchors) && savedAnchors.length === corners) {
      setAnchors(savedAnchors.map((p) => ({ x: p.x, y: p.y })));
    } else {
      setAnchors(generateDefaultAnchors(corners || 4, width, height));
    }
    if (Array.isArray(savedHandles) && savedHandles.length === (corners || 4)) {
      setHandles(
        savedHandles.map((h) => ({
          left: { x: h.left?.x || 0, y: h.left?.y || 0 },
          right: { x: h.right?.x || 0, y: h.right?.y || 0 },
        }))
      );
    } else {
      setHandles(
        Array.from({ length: corners || 4 }, () => ({
          left: { x: 0, y: 0 },
          right: { x: 0, y: 0 },
        }))
      );
    }
  }, [
    savedAnchors,
    savedHandles,
    corners,
    width,
    height,
    generateDefaultAnchors,
  ]);

  // Calculate bounding box of actual Bezier curve path by sampling points
  // This considers where the curve actually goes, not just anchors/handles
  const calculatePathBounds = useCallback((as, hs) => {
    if (!as || as.length === 0) return null;

    // Sample points along the Bézier curve to get accurate bounds
    const samplePoints = [];
    const numSamples = 50; // Number of points to sample along each curve segment
    const N = as.length;

    // For each Bezier curve segment
    for (let i = 0; i < N; i++) {
      const curr = as[i];
      const next = as[(i + 1) % N];
      const currentHandle = hs?.[i] || {
        left: { x: 0, y: 0 },
        right: { x: 0, y: 0 },
      };
      const nextHandle = hs?.[(i + 1) % N] || {
        left: { x: 0, y: 0 },
        right: { x: 0, y: 0 },
      };

      if (!currentHandle || !nextHandle) continue;

      const rOff = currentHandle.right || { x: 0, y: 0 };
      const lOff = nextHandle.left || { x: 0, y: 0 };

      // Sample points along this Bézier curve segment
      for (let t = 0; t <= 1; t += 1 / numSamples) {
        // Bézier curve formula: B(t) = (1-t)³P₀ + 3(1-t)²tP₁ + 3(1-t)t²P₂ + t³P₃
        const p0 = curr;
        const p1 = { x: curr.x + rOff.x, y: curr.y + rOff.y };
        const p2 = { x: next.x + lOff.x, y: next.y + lOff.y };
        const p3 = next;

        const t1 = 1 - t;
        const x =
          t1 * t1 * t1 * p0.x +
          3 * t1 * t1 * t * p1.x +
          3 * t1 * t * t * p2.x +
          t * t * t * p3.x;
        const y =
          t1 * t1 * t1 * p0.y +
          3 * t1 * t1 * t * p1.y +
          3 * t1 * t * t * p2.y +
          t * t * t * p3.y;

        samplePoints.push({ x, y });
      }
    }

    if (samplePoints.length === 0) return null;

    // Calculate bounds from sampled points
    let minX = samplePoints[0].x;
    let minY = samplePoints[0].y;
    let maxX = samplePoints[0].x;
    let maxY = samplePoints[0].y;

    samplePoints.forEach((point) => {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    });

    return { minX, minY, maxX, maxY };
  }, []);

  // Build cubic Bezier closed path through anchors using handles
  const buildBezierPath = useCallback((as, hs, offset = { x: 0, y: 0 }) => {
    const N = as.length;
    if (N === 0 || hs.length !== N) return "";
    let d = `M ${as[0].x + offset.x},${as[0].y + offset.y} `;
    for (let i = 0; i < N; i++) {
      const curr = as[i];
      const next = as[(i + 1) % N];
      const currentHandle = hs[i];
      const nextHandle = hs[(i + 1) % N];
      if (!currentHandle || !nextHandle) continue;
      const rOff = currentHandle.right || { x: 0, y: 0 };
      const lOff = nextHandle.left || { x: 0, y: 0 };
      d +=
        `C ${curr.x + rOff.x + offset.x},${curr.y + rOff.y + offset.y} ` +
        `${next.x + lOff.x + offset.x},${next.y + lOff.y + offset.y} ` +
        `${next.x + offset.x},${next.y + offset.y} `;
    }
    return d + "Z";
  }, []);

  const pathD = useMemo(
    () => buildBezierPath(anchors, handles, contentOffset),
    [anchors, handles, contentOffset, buildBezierPath]
  );

  // Debounced function to update container dimensions and position
  const updateContainerDimensions = useCallback(() => {
    if (isResizingRef.current) return;

    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      const bounds = calculatePathBounds(
        anchorsRef.current,
        handlesRef.current
      );
      if (!bounds) return;

      const margin = Math.max(borderWidth * 2, 10); // Add margin for stroke
      const newWidth = Math.max(
        bounds.maxX - bounds.minX + margin * 2,
        30 // Minimum width
      );
      const newHeight = Math.max(
        bounds.maxY - bounds.minY + margin * 2,
        20 // Minimum height
      );

      // Calculate position adjustment needed if bounds went negative
      const positionDeltaX = bounds.minX < 0 ? bounds.minX : 0;
      const positionDeltaY = bounds.minY < 0 ? bounds.minY : 0;

      // Adjust anchors first if position needs to change
      if (positionDeltaX !== 0 || positionDeltaY !== 0) {
        // Adjust all anchors by the position delta to keep them in the same world position
        setAnchors((as) => {
          const adjusted = as.map((anchor) => ({
            x: anchor.x - positionDeltaX,
            y: anchor.y - positionDeltaY,
          }));
          // Update ref immediately
          anchorsRef.current = adjusted;
          return adjusted;
        });

        // Dispatch position update to move container
        window.dispatchEvent(
          new CustomEvent("componentDropped", {
            detail: {
              id,
              type: "toggle_button",
              x: position.left + positionDeltaX,
              y: position.top + positionDeltaY,
            },
          })
        );

        // Persist the adjusted anchors
        setTimeout(() => {
          onUpdate?.(id, {
            anchors: anchorsRef.current,
            handles: handlesRef.current,
          });
        }, 0);
      }

      // Calculate offset based on adjusted bounds
      // After anchor adjustment, minX/minY should be >= 0
      const adjustedMinX = bounds.minX - positionDeltaX;
      const adjustedMinY = bounds.minY - positionDeltaY;
      const newOffsetX = margin - adjustedMinX;
      const newOffsetY = margin - adjustedMinY;

      isResizingRef.current = true;

      setContentOffset({ x: newOffsetX, y: newOffsetY });
      setDynamicDimensions({ width: newWidth, height: newHeight });

      // Notify parent component about size change
      if (setSize) {
        setSize(id, { width: newWidth, height: newHeight });
      }

      setTimeout(() => {
        isResizingRef.current = false;
      }, 50);
    }, 100); // Debounce to avoid too frequent updates
  }, [
    id,
    setSize,
    borderWidth,
    calculatePathBounds,
    position.left,
    position.top,
    onUpdate,
  ]);

  const isPointInsidePath = (e) => {
    const svgEl = svgRef.current;
    const pathEl = pathRef.current;
    if (!svgEl || !pathEl) return false;
    try {
      const pt = svgEl.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const ctm = svgEl.getScreenCTM();
      if (!ctm) return false;
      const local = pt.matrixTransform(ctm.inverse());
      if (typeof pathEl.isPointInFill === "function") {
        return pathEl.isPointInFill(local);
      }
      const bb = pathEl.getBBox();
      return (
        local.x >= bb.x &&
        local.x <= bb.x + bb.width &&
        local.y >= bb.y &&
        local.y <= bb.y + bb.height
      );
    } catch (err) {
      return false;
    }
  };

  const handleClickInside = (e) => {
    e.stopPropagation();
    if (isSubDragActive) return;
    if (!isPointInsidePath(e)) return;
    setToggleValue?.(id, !toggled);
  };

  const handleResize = (_, dims) => {
    isResizingRef.current = true;

    const scaleX = dims.width / dynamicDimensions.width;
    const scaleY = dims.height / dynamicDimensions.height;

    // Scale the anchors and handles
    setAnchors((as) => as.map((p) => ({ x: p.x * scaleX, y: p.y * scaleY })));
    setHandles((hs) =>
      hs.map((h) => ({
        left: { x: h.left.x * scaleX, y: h.left.y * scaleY },
        right: { x: h.right.x * scaleX, y: h.right.y * scaleY },
      }))
    );

    // Scale the offset as well
    setContentOffset((offset) => ({
      x: offset.x * scaleX,
      y: offset.y * scaleY,
    }));

    setDynamicDimensions(dims);

    // Notify parent and persist
    setSize?.(id, dims);

    // Persist after state updates flush
    setTimeout(() => {
      onUpdate?.(id, {
        anchors: anchorsRef.current,
        handles: handlesRef.current,
      });
      isResizingRef.current = false;
    }, 0);
  };

  const dragStart = () => setIsActive(true);
  const dragStop = (_, d) => {
    setIsActive(false);
    window.dispatchEvent(
      new CustomEvent("componentDropped", {
        detail: { id, type: "toggle_button", x: d.x, y: d.y },
      })
    );
  };

  const textTransform = useMemo(() => {
    // Keep text size independent of canvas zoom
    if (canvasScale === 1) return undefined;
    const x = dynamicDimensions.width / 2;
    const y = dynamicDimensions.height / 2;
    return `translate(${x}, ${y}) scale(${
      1 / canvasScale
    }) translate(${-x}, ${-y})`;
  }, [canvasScale, dynamicDimensions.width, dynamicDimensions.height]);

  // Initialize container dimensions synchronously before first paint
  useLayoutEffect(() => {
    // Only recalculate if we have saved anchors (existing component)
    // For new components, use the provided width/height without adjustment
    if (!savedAnchors || savedAnchors.length === 0) {
      // New component - anchors are already positioned correctly for given dimensions
      // Set offset to 0 so path renders at its designed position
      setContentOffset({ x: 0, y: 0 });
      setDynamicDimensions({ width, height });
      return;
    }

    // Existing component with saved data - recalculate to fit the path
    const bounds = calculatePathBounds(anchors, handles);
    if (!bounds) return;

    const margin = Math.max(borderWidth * 2, 10);
    const newWidth = Math.max(bounds.maxX - bounds.minX + margin * 2, 30);
    const newHeight = Math.max(bounds.maxY - bounds.minY + margin * 2, 20);

    const newOffsetX = margin - bounds.minX;
    const newOffsetY = margin - bounds.minY;

    setContentOffset({ x: newOffsetX, y: newOffsetY });
    setDynamicDimensions({ width: newWidth, height: newHeight });

    if (setSize) {
      setSize(id, { width: newWidth, height: newHeight });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Update dimensions when width/height props change (e.g., from parent)
  // Only update if they're different from our dynamic dimensions
  useEffect(() => {
    if (
      width !== dynamicDimensions.width ||
      height !== dynamicDimensions.height
    ) {
      // Only sync if we're not auto-managing dimensions
      if (!isResizingRef.current) {
        setDynamicDimensions({ width, height });
      }
    }
  }, [width, height, dynamicDimensions.width, dynamicDimensions.height]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <BaseDraggable
      id={id}
      type="toggle_button"
      position={position}
      width={dynamicDimensions.width}
      height={dynamicDimensions.height}
      onDelete={onDelete}
      onResize={handleResize}
      onDragStart={dragStart}
      onDragStop={dragStop}
      className={isActive ? "z-[10000] hover:shadow-none" : ""}
      style={{
        boxShadow: "none",
        backgroundColor: "transparent",
        // padding: "4px",
      }}
      minWidth={30}
      minHeight={20}
      disableDragging={isSubDragActive}
      deleteButtonPosition="-top-5 -right-5"
    >
      <div
        className="relative w-full h-full select-none"
        ref={(node) => {
          // Keep a ref to the wrapper via svgRef.parent as well
          // but we store node for containment checks if needed later
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onDoubleClick?.("toggle_button", id, e);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <svg
          ref={svgRef}
          className="absolute inset-0"
          width="100%"
          height="100%"
          viewBox={`0 0 ${dynamicDimensions.width} ${dynamicDimensions.height}`}
          preserveAspectRatio="none"
          style={{ overflow: "visible" }}
        >
          <path
            ref={pathRef}
            d={pathD}
            fill={toggled ? backgroundColor : "transparent"}
            fillOpacity={opacity}
            stroke={borderColor}
            strokeWidth={borderWidth}
            vectorEffect="non-scaling-stroke"
            style={{ pointerEvents: "none" }}
          />

          <rect
            x="0"
            y="0"
            width={dynamicDimensions.width}
            height={dynamicDimensions.height}
            fill="transparent"
            style={{ cursor: "default" }}
            onClick={handleClickInside}
            onMouseMove={(e) => {
              try {
                const inside = isPointInsidePath(e);
                e.currentTarget.style.cursor = inside ? "pointer" : "default";
              } catch (_) {
                e.currentTarget.style.cursor = "default";
              }
            }}
          />

          <text
            x={dynamicDimensions.width / 2}
            y={dynamicDimensions.height / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fill={fontColor}
            fontSize={fontSize}
            fontFamily={fontFamily}
            fontWeight={fontStyles?.bold ? "bold" : "normal"}
            fontStyle={fontStyles?.italic ? "italic" : "normal"}
            textDecoration={[
              fontStyles?.underline ? "underline" : "",
              fontStyles?.lineThrough ? "line-through" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            transform={textTransform}
            style={{ pointerEvents: "none", userSelect: "none" }}
          >
            {toggled ? "ON" : "OFF"}
          </text>

          {(isHovered || activeAnchor != null) &&
            anchors.map((pt, i) => (
              <circle
                key={i}
                cx={pt.x + contentOffset.x}
                cy={pt.y + contentOffset.y}
                r={6}
                fill={activeAnchor === i ? "#3b82f6" : "white"}
                stroke={activeAnchor === i ? "#3b82f6" : "gray"}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                style={{ cursor: activeAnchor === i ? "move" : "pointer" }}
                onPointerDownCapture={(e) => {
                  e.stopPropagation();
                  const rect = svgRef.current.getBoundingClientRect();
                  const toLocal = (ev) => ({
                    x:
                      ((ev.clientX - rect.left) / rect.width) *
                        dynamicDimensions.width -
                      contentOffset.x,
                    y:
                      ((ev.clientY - rect.top) / rect.height) *
                        dynamicDimensions.height -
                      contentOffset.y,
                  });
                  setActiveAnchor(i);
                  setIsSubDragActive(true);

                  // Initialize default handles if both are zero
                  const prev =
                    anchors[(i - 1 + anchors.length) % anchors.length];
                  const next = anchors[(i + 1) % anchors.length];
                  const dirTo = (from, to) => {
                    const dx = to.x - from.x;
                    const dy = to.y - from.y;
                    const len = Math.hypot(dx, dy) || 1;
                    return { x: dx / len, y: dy / len };
                  };
                  const h = handles[i] || {
                    left: { x: 0, y: 0 },
                    right: { x: 0, y: 0 },
                  };
                  if (
                    h.left.x === 0 &&
                    h.left.y === 0 &&
                    h.right.x === 0 &&
                    h.right.y === 0
                  ) {
                    const L = 30;
                    const toPrev = dirTo(anchors[i], prev);
                    const toNext = dirTo(anchors[i], next);
                    setHandles((hs) => {
                      const copy = hs.map((hh) => ({
                        left: { ...hh.left },
                        right: { ...hh.right },
                      }));
                      copy[i] = {
                        left: { x: toPrev.x * L, y: toPrev.y * L },
                        right: { x: toNext.x * L, y: toNext.y * L },
                      };
                      return copy;
                    });
                  }

                  const onMove = (ev) => {
                    const { x, y } = toLocal(ev);
                    // No constraints - allow free movement
                    setAnchors((as) => {
                      const copy = as.map((p) => ({ ...p }));
                      copy[i] = { x, y };
                      return copy;
                    });
                  };
                  const onUp = () => {
                    window.removeEventListener("pointermove", onMove);
                    window.removeEventListener("pointerup", onUp);
                    setIsSubDragActive(false);
                    // Update container dimensions to fit new anchor positions
                    updateContainerDimensions();
                    onUpdate?.(id, {
                      anchors: anchorsRef.current,
                      handles: handlesRef.current,
                    });
                  };
                  window.addEventListener("pointermove", onMove);
                  window.addEventListener("pointerup", onUp);
                }}
              />
            ))}

          {activeAnchor != null &&
            handles[activeAnchor] &&
            ["left", "right"].map((side) => {
              const off = handles[activeAnchor][side];
              const base = anchors[activeAnchor];
              return (
                <g key={side}>
                  <line
                    x1={base.x + contentOffset.x}
                    y1={base.y + contentOffset.y}
                    x2={base.x + off.x + contentOffset.x}
                    y2={base.y + off.y + contentOffset.y}
                    stroke="#3b82f6"
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: "none" }}
                  />
                  <circle
                    cx={base.x + off.x + contentOffset.x}
                    cy={base.y + off.y + contentOffset.y}
                    r={5}
                    fill="#3b82f6"
                    stroke="#3b82f6"
                    strokeWidth={1}
                    style={{ cursor: "move" }}
                    onPointerDownCapture={(e) => {
                      e.stopPropagation();
                      const rect = svgRef.current.getBoundingClientRect();
                      const toLocal = (ev) => ({
                        x:
                          ((ev.clientX - rect.left) / rect.width) *
                            dynamicDimensions.width -
                          contentOffset.x,
                        y:
                          ((ev.clientY - rect.top) / rect.height) *
                            dynamicDimensions.height -
                          contentOffset.y,
                      });
                      const idx = activeAnchor;
                      setIsSubDragActive(true);
                      const onMove = (ev) => {
                        const { x, y } = toLocal(ev);
                        const nx = x - anchors[idx].x;
                        const ny = y - anchors[idx].y;
                        setHandles((hs) => {
                          const copy = hs.map((hh) => ({
                            left: { ...hh.left },
                            right: { ...hh.right },
                          }));
                          copy[idx] = {
                            ...copy[idx],
                            [side]: { x: nx, y: ny },
                          };
                          return copy;
                        });
                      };
                      const onUp = () => {
                        window.removeEventListener("pointermove", onMove);
                        window.removeEventListener("pointerup", onUp);
                        setIsSubDragActive(false);
                        // Update container to fit the curve path (not handle positions)
                        updateContainerDimensions();
                        onUpdate?.(id, {
                          anchors: anchorsRef.current,
                          handles: handlesRef.current,
                        });
                      };
                      window.addEventListener("pointermove", onMove);
                      window.addEventListener("pointerup", onUp);
                    }}
                  />
                </g>
              );
            })}
        </svg>
      </div>
    </BaseDraggable>
  );
}
